import { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import { useAuth } from '../../auth/AuthProvider';
import { useBuildings, useClubMutation, useDoors, useLocations } from '../../data/hooks';
import { Badge, Button, ErrorState, Field, Loading, PageHeader, Panel, useToast } from '../../components/ui';
import { LocationDialog } from '../../components/DoorManagement';
import { formatAddress } from '../../lib/address';
import type { Location } from '../../lib/types';
import { ClubAdminsPanel } from '../../components/ClubAdmins';
import { ClubInstallersPanel } from '../../components/ClubInstallers';
import { can } from '../../lib/roles';
import { errorText } from '../../lib/errors';
import { alertThreshold } from '../../lib/battery';

const schema = z.object({
  name: z.string().trim().min(2, 'Vul de clubnaam in.'),
  kvk_number: z.union([z.literal(''), z.string().trim().regex(/^\d{8}$/, 'Een KvK-nummer heeft 8 cijfers.')]),
  log_retention_days: z.coerce.number().int().min(30, 'Minimaal 30 dagen.').max(730, 'Maximaal 730 dagen.'),
  revoke_alert_hours: z.coerce.number().int().min(1, 'Minimaal 1 uur.').max(336, 'Maximaal 14 dagen (336 uur).'),
  battery_alert_pct: z.coerce
    .number({ invalid_type_error: 'Vul een percentage in.' })
    .int('Gebruik een heel getal.')
    .min(5, 'Minimaal 5%.')
    .max(80, 'Maximaal 80%.'),
});

export function SettingsPage() {
  const { org, orgId, role, readOnly } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const { session } = useAuth();
  const editable = can('settings.edit', role) && !readOnly;
  const [form, setForm] = useState({
    name: org.name,
    kvk_number: org.kvk_number ?? '',
    log_retention_days: String(org.log_retention_days),
    revoke_alert_hours: String(org.revoke_alert_hours),
    battery_alert_pct: String(alertThreshold(org)),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useClubMutation(orgId, (patch: Parameters<typeof repo.updateOrganization>[1]) => repo.updateOrganization(orgId, patch));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const i of parsed.error.issues) errs[String(i.path[0])] = i.message;
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      await save.mutateAsync({ ...parsed.data, kvk_number: parsed.data.kvk_number || null });
      toast('Instellingen opgeslagen');
    } catch (err) {
      setErrors({ form: errorText(err, 'Opslaan mislukt') });
    }
  };

  const bind = (key: keyof typeof form) => ({
    className: 'input',
    value: form[key],
    disabled: !editable,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div className="stack-lg">
      <PageHeader title="Instellingen" intro="Clubgegevens, bewaartermijnen en wie het portaal mag beheren." />
      <Panel title="Club">
        <form className="panel-pad stack" onSubmit={submit} noValidate style={{ maxWidth: 560 }}>
          <Field label="Clubnaam" error={errors.name}>
            {(p) => <input {...p} {...bind('name')} />}
          </Field>
          <Field label="KvK-nummer" error={errors.kvk_number}>
            {(p) => <input {...p} {...bind('kvk_number')} inputMode="numeric" />}
          </Field>
          <Field label="Logboek bewaren (dagen)" hint="Tussen 30 en 730 dagen. Daarna worden events verwijderd (AVG)." error={errors.log_retention_days}>
            {(p) => <input {...p} {...bind('log_retention_days')} type="number" min={30} max={730} style={{ maxWidth: 160 }} />}
          </Field>
          <Field
            label="Melding openstaande intrekking na (uur)"
            hint="Staat een intrekking langer open, dan krijg je een melding om met de app langs de deur te lopen."
            error={errors.revoke_alert_hours}
          >
            {(p) => <input {...p} {...bind('revoke_alert_hours')} type="number" min={1} max={336} style={{ maxWidth: 160 }} />}
          </Field>
          <Field
            label="E-mail bij lage batterij vanaf (%)"
            hint="Clubbeheerders krijgen een e-mail als de batterij van een slot op of onder dit percentage komt. Tussen 5 en 80; standaard 25."
            error={errors.battery_alert_pct}
          >
            {(p) => <input {...p} {...bind('battery_alert_pct')} type="number" min={5} max={80} style={{ maxWidth: 160 }} />}
          </Field>
          {errors.form && (
            <div className="alert alert-danger" role="alert">
              {errors.form}
            </div>
          )}
          {editable && (
            <div>
              <Button variant="primary" type="submit" disabled={save.isPending}>
                Opslaan
              </Button>
            </div>
          )}
        </form>
      </Panel>

      <LocationsPanel orgId={orgId} editable={editable} />

      <ClubAdminsPanel
        orgId={orgId}
        orgName={org.name}
        editable={can('admins.manage', role) && !readOnly}
        currentProfileId={session?.profile.id ?? null}
      />

      <ClubInstallersPanel orgId={orgId} visible={can('installer.club_assign', role) || role === 'support'} />

      <Panel title="Privacy">
        <dl className="kv panel-pad">
          <dt>Verwerkersovereenkomst</dt>
          <dd>
            <Badge tone="yellow">Nog niet in het portaal</Badge> <span className="muted small">Je ontvangt hem van Antreja per e-mail.</span>
          </dd>
          <dt>Vingerafdruk toestaan</dt>
          <dd>
            <span className="muted small">Per persoon met vastgelegde toestemming. Een clubbrede schakelaar volgt.</span>
          </dd>
        </dl>
      </Panel>
    </div>
  );
}

/** Locaties met hun adres (wijzigen via save_location); gebouwen en deuren staan op de pagina Deuren. */
function LocationsPanel({ orgId, editable }: { orgId: string; editable: boolean }) {
  const locations = useLocations(orgId);
  const buildings = useBuildings(orgId);
  const doors = useDoors(orgId);
  const [edit, setEdit] = useState<Location | 'new' | null>(null);
  const body = () => {
    if (locations.isLoading || buildings.isLoading || doors.isLoading) return <Loading />;
    const err = locations.error ?? buildings.error ?? doors.error;
    if (err) return <ErrorState error={err} />;
    if (locations.data!.length === 0) return <p className="tile-empty">Nog geen locaties.</p>;
    return (
      <ul className="tile-list">
        {locations.data!.map((l) => {
          const address = formatAddress(l);
          const blds = buildings.data!.filter((b) => b.location_id === l.id && !b.archived_at).map((b) => b.name);
          return (
            <li key={l.id}>
              <span>
                <span className="primary">{l.name}</span>
                <span className="cell-sub">
                  {address || <Badge tone="yellow">Nog geen adres</Badge>}
                </span>
                <span className="cell-sub">{blds.length ? `Gebouwen: ${blds.join(', ')}` : 'Nog geen gebouwen'}</span>
              </span>
              {editable && (
                <Button size="sm" onClick={() => setEdit(l)}>
                  Adres wijzigen
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    );
  };
  return (
    <Panel
      title="Locaties en adressen"
      description={
        <>
          Het adres van elk sportpark of terrein. Gebouwen (kantine, kleedkamers, ballenhok) en deuren beheer je op de pagina{' '}
          <Link to={`/c/${orgId}/deuren`}>Deuren</Link>.
        </>
      }
      actions={
        editable && (
          <Button size="sm" onClick={() => setEdit('new')}>
            Locatie toevoegen
          </Button>
        )
      }
    >
      {body()}
      {edit && (
        <LocationDialog
          location={edit === 'new' ? null : edit}
          hasDoors={edit !== 'new' && (doors.data ?? []).some((d) => d.location_id === edit.id)}
          onClose={() => setEdit(null)}
        />
      )}
    </Panel>
  );
}
